import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PayrollSetupList } from './PayrollSetupList'
import {
  FETCH_COMPANY_ONBOARDING_STATUS_KEY,
  fetchCompanyOnboardingStatus,
  ONBOARDING_FLOWS,
  POST_ACCEPT_COMPANY_TOS_KEY,
  postAcceptCompanyTOS,
  postCreateGustoFlow,
  PUT_FINISH_ONBOARDING_KEY,
  putFinishOnboarding,
} from '../payrollActions'
import {
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import { Button, Card, Dialog, Separator, List, Text, Embed } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetching, selectIsFetchingForKeys } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'

export const PayrollEnrollGustoFlow = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.gustoFlow)

  const isFetchingStatus = useReselector(
    getIsFetching,
    FETCH_COMPANY_ONBOARDING_STATUS_KEY
  )
  const isPosting = useReselector(selectIsFetchingForKeys, [
    POST_ACCEPT_COMPANY_TOS_KEY,
    PUT_FINISH_ONBOARDING_KEY,
  ])

  const [link, setLink] = useState<string>()
  const [isComplete, setIsComplete] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const checkCompletion = useCallback(async () => {
    const res = await fetchCompanyOnboardingStatus()(dispatch)

    const isCompleteRes =
      Boolean(res) && !res?.onboarding_steps.some((step) => !step.completed)

    setIsComplete(isCompleteRes)
    return isCompleteRes
  }, [dispatch])

  useEffect(() => {
    const fetch = async () => {
      const complete = await checkCompletion()
      if (complete) {
        return
      }

      const res = await postCreateGustoFlow(ONBOARDING_FLOWS)(dispatch)

      if (res) {
        setLink(res.url)
      }
    }

    fetch()
  }, [checkCompletion, dispatch])

  const closeModal = useCallback(() => {
    setIsOpen(false)
    checkCompletion()
  }, [checkCompletion])

  const completeSignup = useCallback(async () => {
    const finishOnboardingRes = await putFinishOnboarding()(dispatch)

    // The user accepted TOS by continuing on /payroll/enroll/overview but the company isn't created at that time.
    // Make it here instead
    const acceptTermsRes = await postAcceptCompanyTOS()(dispatch)

    if (finishOnboardingRes && acceptTermsRes) {
      track(
        'completed payroll step - gusto iframe onboarding flows and tos acceptance'
      )

      navigate(GEP_ENROLL_PATHS.complete)
    }
  }, [dispatch, navigate, track])

  return (
    <div>
      <Card>
        <div className="flex flex-col gap-6">
          <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>
          <Separator />
          <PrefilledNoticeContainer />

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-2/3 flex flex-col gap-6">
              {isComplete ? (
                <Text variant={TypographyStyles.H2}>
                  Select <b>Complete</b> to finish your onboarding
                </Text>
              ) : (
                <>
                  <Text variant={TypographyStyles.H2}>Finish up on Gusto</Text>
                  <Text variant={TypographyStyles.BODY_SM}>
                    You&apos;re almost done! We have a few more steps that need
                    your attention.
                  </Text>
                  <List className="list-disc list-inside">
                    <Text>Add bank account</Text>
                    <Text>State tax information</Text>
                    <Text>Sign Documents</Text>
                  </List>
                  <Text variant={TypographyStyles.BODY_SM}>
                    You will be redirected to Gusto in order to complete the
                    final steps of the payroll process.
                  </Text>
                </>
              )}

              <div className="flex gap-4 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => navigate(GEP_ENROLL_PATHS.schedule)}
                >
                  Back
                </Button>
                <div className="flex-1" />
                {link && !isComplete && (
                  <Button
                    onClick={() => setIsOpen(true)}
                    loading={isFetchingStatus}
                  >
                    Finish up on Gusto
                  </Button>
                )}
                {isComplete && (
                  <Button onClick={completeSignup} loading={isPosting}>
                    Complete
                  </Button>
                )}
              </div>
            </div>

            <div className="lg:w-1/3">
              <PayrollSetupList />
            </div>
          </div>
        </div>

        <Dialog open={isOpen} onOpenChange={setIsOpen} onClose={closeModal}>
          <Dialog.Content className="w-full max-w-full h-[95vh] overflow-hidden">
            <Embed
              src={link ?? ''}
              scrolling
              title="Gusto Payroll Enrollment"
              className="h-[90%]"
            />
          </Dialog.Content>
        </Dialog>
      </Card>
    </div>
  )
}
